import { saveDevicePkcs8Backup } from './accountDeviceSigning'

export const VAULT_UNRECOVERABLE_WARNING =
  'If both the account password and the recovery key are lost, funds cannot be recovered.'

const VAULT_VERSION = 'client-vault-v1' as const
const CIPHER_NAME = 'AES-256-GCM' as const
const KDF_NAME = 'PBKDF2-SHA256' as const
const DEFAULT_ITERATIONS = 310_000
const DEFAULT_UNLOCK_TTL_MS = 15 * 60 * 1_000

export type VaultKeySlotKind = 'password' | 'recovery_key' | 'passkey_prf' | 'login'

export type VaultKDF = {
  name: typeof KDF_NAME
  iterations: number
  saltB64: string
}

export type VaultCipher = {
  name: typeof CIPHER_NAME
  nonceB64: string
}

export type VaultKeySlot = {
  id: string
  kind: VaultKeySlotKind
  kdf: VaultKDF
  cipher: VaultCipher
  wrappedKeyB64: string
  /** WebAuthn credential id for passkey_prf slots. Not a secret. */
  credentialIdB64?: string
}

export type ClientVaultPayload = {
  version: typeof VAULT_VERSION
  cipher: VaultCipher
  kdf: VaultKDF
  seedCiphertextB64: string
  keySlots: VaultKeySlot[]
  warning: typeof VAULT_UNRECOVERABLE_WARNING
}

export type EncryptSeedOptions = {
  password?: string
  recoveryKey?: string
  passkeyPrf?: Uint8Array
  loginSecret?: Uint8Array
  iterations?: number
  randomBytes?: (size: number) => Uint8Array
}

export type DecryptSeedOptions = {
  password?: string
  recoveryKey?: string
  passkeyPrf?: Uint8Array
  loginSecret?: Uint8Array
}

export type UnlockVaultSessionOptions = DecryptSeedOptions & {
  nowMs?: number
  ttlMs?: number
}

export type DecryptSessionOptions = {
  nowMs?: number
}

export async function encryptSeedForVault(
  seedMaterial: string,
  options: EncryptSeedOptions
): Promise<ClientVaultPayload> {
  if (!seedMaterial) {
    throw new Error('seed material required')
  }
  const slots = slotInputs(options)
  const hasUnlock = slots.some((slot) => slot.kind === 'password' || slot.kind === 'passkey_prf')
  const hasRecovery = slots.some(
    (slot) => slot.kind === 'recovery_key' || slot.kind === 'passkey_prf'
  )
  if (!hasUnlock) {
    throw new Error('password or passkey required')
  }
  if (!hasRecovery) {
    throw new Error('recovery path required before vault storage')
  }

  const randomBytes = options.randomBytes ?? secureRandomBytes
  const iterations = options.iterations ?? DEFAULT_ITERATIONS
  const dekBytes = randomBytes(32)
  const seedKey = await importAesKey(dekBytes, ['encrypt'])
  const seedNonce = randomBytes(12)
  const seedCiphertext = new Uint8Array(
    await subtle().encrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(seedNonce) },
      seedKey,
      toArrayBuffer(utf8(seedMaterial))
    )
  )

  const keySlots: VaultKeySlot[] = []
  for (const slot of slots) {
    const salt = randomBytes(16)
    const nonce = randomBytes(12)
    const wrappingKey = await deriveWrappingKey(slot.secret, salt, iterations)
    const wrappedKey = new Uint8Array(
      await subtle().encrypt(
        { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
        wrappingKey,
        toArrayBuffer(dekBytes)
      )
    )
    keySlots.push({
      id: `slot-${slot.kind}`,
      kind: slot.kind,
      kdf: {
        name: KDF_NAME,
        iterations,
        saltB64: toBase64Url(salt),
      },
      cipher: {
        name: CIPHER_NAME,
        nonceB64: toBase64Url(nonce),
      },
      wrappedKeyB64: toBase64Url(wrappedKey),
    })
  }
  dekBytes.fill(0)

  return {
    version: VAULT_VERSION,
    cipher: {
      name: CIPHER_NAME,
      nonceB64: toBase64Url(seedNonce),
    },
    kdf: keySlots[0]?.kdf ?? {
      name: KDF_NAME,
      iterations,
      saltB64: '',
    },
    seedCiphertextB64: toBase64Url(seedCiphertext),
    keySlots,
    warning: VAULT_UNRECOVERABLE_WARNING,
  }
}

export async function decryptSeedFromVault(
  payload: ClientVaultPayload,
  options: DecryptSeedOptions
): Promise<string> {
  const dekBytes = await unwrapVaultDEK(payload, options)
  try {
    return await decryptSeedWithDEK(payload, dekBytes)
  } finally {
    dekBytes.fill(0)
  }
}

export async function unlockVaultSession(
  payload: ClientVaultPayload,
  options: UnlockVaultSessionOptions
): Promise<ClientVaultUnlockSession> {
  const dekBytes = await unwrapVaultDEK(payload, options)
  const unlockedAtMs = options.nowMs ?? Date.now()
  const ttlMs = options.ttlMs ?? DEFAULT_UNLOCK_TTL_MS
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    dekBytes.fill(0)
    throw new Error('unlock ttl must be positive')
  }
  return new ClientVaultUnlockSession(payload, dekBytes, unlockedAtMs, ttlMs)
}

export class ClientVaultUnlockSession {
  #payload: ClientVaultPayload
  #dekBytes: Uint8Array | null
  #unlockedAtMs: number
  #expiresAtMs: number

  constructor(
    payload: ClientVaultPayload,
    dekBytes: Uint8Array,
    unlockedAtMs: number,
    ttlMs: number
  ) {
    this.#payload = payload
    this.#dekBytes = new Uint8Array(dekBytes)
    dekBytes.fill(0)
    this.#unlockedAtMs = unlockedAtMs
    this.#expiresAtMs = unlockedAtMs + ttlMs
  }

  get unlockedAtMs(): number {
    return this.#unlockedAtMs
  }

  get expiresAtMs(): number {
    return this.#expiresAtMs
  }

  isUnlocked(nowMs = Date.now()): boolean {
    if (!this.#dekBytes) {
      return false
    }
    if (nowMs > this.#expiresAtMs) {
      this.lock()
      return false
    }
    return true
  }

  async decryptSeedMaterial(options: DecryptSessionOptions = {}): Promise<string> {
    const nowMs = options.nowMs ?? Date.now()
    if (!this.isUnlocked(nowMs) || !this.#dekBytes) {
      throw new Error(
        nowMs > this.#expiresAtMs ? 'vault unlock session expired' : 'vault unlock session locked'
      )
    }
    return decryptSeedWithDEK(this.#payload, this.#dekBytes)
  }

  lock(): void {
    this.#dekBytes?.fill(0)
    this.#dekBytes = null
  }

  toJSON(): Record<string, never> {
    return {}
  }
}

// The vault/signing CSP lives in lib/csp.ts, the single source of truth for
// every policy the app emits.
export { buildVaultSigningCSP } from './csp'

type SlotInput = {
  kind: VaultKeySlotKind
  secret: Uint8Array
}

async function unwrapVaultDEK(
  payload: ClientVaultPayload,
  options: DecryptSeedOptions
): Promise<Uint8Array> {
  if (payload.version !== VAULT_VERSION) {
    throw new Error(`unsupported vault version: ${payload.version}`)
  }
  const inputs = slotInputs(options)
  const available = new Map(inputs.map((slot) => [slot.kind, slot.secret]))
  try {
    for (const slot of payload.keySlots) {
      const secret = available.get(slot.kind)
      if (!secret) {
        continue
      }
      try {
        const wrappingKey = await deriveWrappingKey(
          secret,
          fromBase64Url(slot.kdf.saltB64),
          slot.kdf.iterations
        )
        return new Uint8Array(
          await subtle().decrypt(
            { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(slot.cipher.nonceB64)) },
            wrappingKey,
            toArrayBuffer(fromBase64Url(slot.wrappedKeyB64))
          )
        )
      } catch {}
    }
    throw new Error('unable to decrypt vault with provided user-held secret')
  } finally {
    for (const input of inputs) input.secret.fill(0)
  }
}

async function decryptSeedWithDEK(
  payload: ClientVaultPayload,
  dekBytes: Uint8Array
): Promise<string> {
  const seedKey = await importAesKey(dekBytes, ['decrypt'])
  const seedBytes = new Uint8Array(
    await subtle().decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(payload.cipher.nonceB64)) },
      seedKey,
      toArrayBuffer(fromBase64Url(payload.seedCiphertextB64))
    )
  )
  return new TextDecoder().decode(seedBytes)
}

function slotInputs(options: EncryptSeedOptions | DecryptSeedOptions): SlotInput[] {
  const slots: SlotInput[] = []
  if (options.password) {
    slots.push({ kind: 'password', secret: utf8(options.password) })
  }
  if (options.recoveryKey) {
    slots.push({ kind: 'recovery_key', secret: utf8(options.recoveryKey) })
  }
  if (options.passkeyPrf && options.passkeyPrf.byteLength > 0) {
    slots.push({ kind: 'passkey_prf', secret: new Uint8Array(options.passkeyPrf) })
  }
  if (options.loginSecret && options.loginSecret.byteLength === 32) {
    slots.push({ kind: 'login', secret: new Uint8Array(options.loginSecret) })
  }
  return slots
}

export function generateLoginSecret(): Uint8Array {
  return secureRandomBytes(32)
}

export function loginSecretToB64(secret: Uint8Array): string {
  return toBase64Url(secret)
}

async function deriveWrappingKey(
  secret: Uint8Array,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> {
  const baseKey = await subtle().importKey('raw', toArrayBuffer(secret), 'PBKDF2', false, [
    'deriveKey',
  ])
  return subtle().deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: toArrayBuffer(salt),
      iterations,
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

async function importAesKey(keyBytes: Uint8Array, usages: KeyUsage[]): Promise<CryptoKey> {
  return subtle().importKey(
    'raw',
    toArrayBuffer(keyBytes),
    { name: 'AES-GCM', length: 256 },
    false,
    usages
  )
}

function subtle(): SubtleCrypto {
  const crypto = globalThis.crypto
  if (!crypto?.subtle) {
    throw new Error('WebCrypto subtle crypto is required')
  }
  return crypto.subtle
}

function secureRandomBytes(size: number): Uint8Array {
  const crypto = globalThis.crypto
  if (!crypto?.getRandomValues) {
    throw new Error('WebCrypto random source is required')
  }
  const out = new Uint8Array(size)
  crypto.getRandomValues(out)
  return out
}

function utf8(value: string): Uint8Array {
  return new TextEncoder().encode(value)
}

function toBase64Url(bytes: Uint8Array): string {
  const base64 =
    typeof Buffer !== 'undefined'
      ? Buffer.from(bytes).toString('base64')
      : btoa(String.fromCharCode(...bytes))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromBase64Url(value: string): Uint8Array {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=')
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(padded, 'base64'))
  }
  return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0))
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

// ---------------------------------------------------------------------------
// Account-level vault (one DEK per ACCOUNT).
//
// The account keyring wraps a single data-encryption key into user-held slots
// (password / recovery key). One unlock exposes the DEK for the whole account:
// every wallet seed is then encrypted/decrypted with that DEK directly — no
// per-wallet password. The keyring (ciphertext only) is stored server-side;
// the DEK lives exclusively in browser memory (AccountVaultSession).
// ---------------------------------------------------------------------------

export const ACCOUNT_VAULT_VERSION = 'account-vault-v1' as const

export type AccountKeyring = {
  version: typeof ACCOUNT_VAULT_VERSION
  kdfName: typeof KDF_NAME
  keySlots: VaultKeySlot[]
}

export type AccountSecretEnvelope = {
  version: typeof VAULT_VERSION
  cipherName: typeof CIPHER_NAME
  nonceB64: string
  ciphertextB64: string
}

// createAccountKeyring mints the account DEK and wraps it into slots. Same
// mandatory-recovery gate as the per-wallet flow: password-only is refused.
export async function createAccountKeyring(
  options: EncryptSeedOptions
): Promise<{ keyring: AccountKeyring; dekBytes: Uint8Array }> {
  const slots = slotInputs(options)
  const hasUnlock = slots.some((slot) => slot.kind === 'password' || slot.kind === 'passkey_prf')
  const hasRecovery = slots.some(
    (slot) => slot.kind === 'recovery_key' || slot.kind === 'passkey_prf'
  )
  if (!hasUnlock) {
    throw new Error('password or passkey required')
  }
  if (!hasRecovery) {
    throw new Error('recovery path required before vault storage')
  }

  const randomBytes = options.randomBytes ?? secureRandomBytes
  const iterations = options.iterations ?? DEFAULT_ITERATIONS
  const dekBytes = randomBytes(32)

  const keySlots: VaultKeySlot[] = []
  for (const slot of slots) {
    const salt = randomBytes(16)
    const nonce = randomBytes(12)
    const wrappingKey = await deriveWrappingKey(slot.secret, salt, iterations)
    const wrappedKey = new Uint8Array(
      await subtle().encrypt(
        { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
        wrappingKey,
        toArrayBuffer(dekBytes)
      )
    )
    keySlots.push({
      id: `slot-${slot.kind}`,
      kind: slot.kind,
      kdf: { name: KDF_NAME, iterations, saltB64: toBase64Url(salt) },
      cipher: { name: CIPHER_NAME, nonceB64: toBase64Url(nonce) },
      wrappedKeyB64: toBase64Url(wrappedKey),
    })
  }

  return {
    keyring: { version: ACCOUNT_VAULT_VERSION, kdfName: KDF_NAME, keySlots },
    dekBytes,
  }
}

// unlockAccountKeyring unwraps the account DEK with any provided user-held
// secret (password or recovery key).
export async function unlockAccountKeyring(
  keyring: AccountKeyring,
  options: DecryptSeedOptions
): Promise<Uint8Array> {
  if (keyring.version !== ACCOUNT_VAULT_VERSION) {
    throw new Error(`unsupported account vault version: ${keyring.version}`)
  }
  const inputs = slotInputs(options)
  const available = new Map(inputs.map((slot) => [slot.kind, slot.secret]))
  try {
    for (const slot of keyring.keySlots) {
      const secret = available.get(slot.kind)
      if (!secret) {
        continue
      }
      try {
        const wrappingKey = await deriveWrappingKey(
          secret,
          fromBase64Url(slot.kdf.saltB64),
          slot.kdf.iterations
        )
        return new Uint8Array(
          await subtle().decrypt(
            { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(slot.cipher.nonceB64)) },
            wrappingKey,
            toArrayBuffer(fromBase64Url(slot.wrappedKeyB64))
          )
        )
      } catch {}
    }
    throw new Error('unable to unlock account vault with provided secret')
  } finally {
    for (const input of inputs) input.secret.fill(0)
  }
}

export async function encryptSecretWithDEK(
  dekBytes: Uint8Array,
  secret: string
): Promise<AccountSecretEnvelope> {
  if (!secret) {
    throw new Error('secret required')
  }
  const key = await importAesKey(dekBytes, ['encrypt'])
  const nonce = secureRandomBytes(12)
  const ciphertext = new Uint8Array(
    await subtle().encrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
      key,
      toArrayBuffer(utf8(secret))
    )
  )
  return {
    version: VAULT_VERSION,
    cipherName: CIPHER_NAME,
    nonceB64: toBase64Url(nonce),
    ciphertextB64: toBase64Url(ciphertext),
  }
}

export async function decryptSecretWithDEK(
  dekBytes: Uint8Array,
  envelope: Pick<AccountSecretEnvelope, 'nonceB64' | 'ciphertextB64'>
): Promise<string> {
  const key = await importAesKey(dekBytes, ['decrypt'])
  const plain = new Uint8Array(
    await subtle().decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(envelope.nonceB64)) },
      key,
      toArrayBuffer(fromBase64Url(envelope.ciphertextB64))
    )
  )
  return new TextDecoder().decode(plain)
}

// AccountVaultSession keeps the unlocked account DEK in memory only (never
// serialized/persisted), zeroizes on lock/expiry — one unlock serves every
// wallet operation until the session ends.
export class AccountVaultSession {
  #dekBytes: Uint8Array | null
  #expiresAtMs: number

  constructor(dekBytes: Uint8Array, ttlMs = DEFAULT_UNLOCK_TTL_MS, nowMs = Date.now()) {
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
      dekBytes.fill(0)
      throw new Error('unlock ttl must be positive')
    }
    this.#dekBytes = new Uint8Array(dekBytes)
    dekBytes.fill(0)
    this.#expiresAtMs = nowMs + ttlMs
  }

  isUnlocked(nowMs = Date.now()): boolean {
    if (!this.#dekBytes) {
      return false
    }
    if (nowMs > this.#expiresAtMs) {
      this.lock()
      return false
    }
    return true
  }

  async encryptSecret(secret: string): Promise<AccountSecretEnvelope> {
    if (!this.isUnlocked() || !this.#dekBytes) {
      throw new Error('account vault session locked')
    }
    return encryptSecretWithDEK(this.#dekBytes, secret)
  }

  async decryptSecret(
    envelope: Pick<AccountSecretEnvelope, 'nonceB64' | 'ciphertextB64'>
  ): Promise<string> {
    if (!this.isUnlocked() || !this.#dekBytes) {
      throw new Error('account vault session locked')
    }
    return decryptSecretWithDEK(this.#dekBytes, envelope)
  }

  lock(): void {
    this.#dekBytes?.fill(0)
    this.#dekBytes = null
  }

  toJSON(): Record<string, never> {
    return {}
  }
}

// ---------------------------------------------------------------------------
// Account vault v2 — ASYMMETRIC envelope (ECIES over ECDH P-256).
//
// Why a keypair instead of one symmetric DEK: with a DEK the same key both
// writes and reads a seed, so ADDING a wallet forced an unlock. Here the
// public half is stored in the clear, so create/import encrypt to it with NO
// password at all. Only decryption — revealing a seed, and later signing —
// needs the private half, which stays wrapped in the password/recovery slots.
//
// Envelope layout (matches the backend account-ecies-v1 CHECK):
//   ciphertext = ephemeral_public_key(65 bytes, raw P-256) || AES-256-GCM(seed)
// ---------------------------------------------------------------------------

export const ACCOUNT_VAULT_V2 = 'account-vault-v2' as const

const ECDH_CURVE = 'P-256' as const
const ECIES_HKDF_INFO = 'wils-account-vault-v2/ecies'
const EPK_BYTES = 65

export type AccountKeyringV2 = {
  version: typeof ACCOUNT_VAULT_V2
  kdfName: typeof KDF_NAME
  keySlots: VaultKeySlot[]
  publicKeyB64: string
}

export class AccountVaultSecretMismatchError extends Error {
  constructor() {
    super('unable to unlock account vault with provided secret')
    this.name = 'AccountVaultSecretMismatchError'
  }
}

export type PrepareAccountVaultPasswordOptions = {
  iterations?: number
  randomBytes?: (size: number) => Uint8Array
  nowMs?: number
  ttlMs?: number
}

// A successful account login may happen before first-time vault setup (the
// user still has to save the recovery key) or before a one-time password-slot
// migration. Keep only the non-extractable PBKDF2 result for that short gap,
// never the raw account password. The derived key is scoped to one random salt
// and cannot be exported or reused as the account credential.
export class PreparedAccountVaultPassword {
  #wrappingKey: CryptoKey | null
  readonly #kdf: VaultKDF
  readonly #expiresAtMs: number

  constructor(wrappingKey: CryptoKey, kdf: VaultKDF, expiresAtMs: number) {
    this.#wrappingKey = wrappingKey
    this.#kdf = { ...kdf }
    this.#expiresAtMs = expiresAtMs
  }

  isUsable(nowMs = Date.now()): boolean {
    if (!this.#wrappingKey) return false
    if (nowMs > this.#expiresAtMs) {
      this.dispose()
      return false
    }
    return true
  }

  async wrapPrivateKey(
    privatePkcs8: Uint8Array,
    slotID: string,
    randomBytes: (size: number) => Uint8Array = secureRandomBytes
  ): Promise<VaultKeySlot> {
    if (!this.isUsable() || !this.#wrappingKey) {
      throw new Error('account password confirmation expired')
    }
    const nonce = randomBytes(12)
    const wrapped = new Uint8Array(
      await subtle().encrypt(
        { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
        this.#wrappingKey,
        toArrayBuffer(privatePkcs8)
      )
    )
    return {
      id: slotID,
      kind: 'password',
      kdf: { ...this.#kdf },
      cipher: { name: CIPHER_NAME, nonceB64: toBase64Url(nonce) },
      wrappedKeyB64: toBase64Url(wrapped),
    }
  }

  dispose(): void {
    this.#wrappingKey = null
  }

  toJSON(): Record<string, never> {
    return {}
  }
}

export async function prepareAccountVaultPassword(
  accountPassword: string,
  options: PrepareAccountVaultPasswordOptions = {}
): Promise<PreparedAccountVaultPassword> {
  if (!accountPassword) throw new Error('account password required')
  const iterations = options.iterations ?? DEFAULT_ITERATIONS
  const randomBytes = options.randomBytes ?? secureRandomBytes
  const salt = randomBytes(16)
  const secret = utf8(accountPassword)
  let wrappingKey: CryptoKey
  try {
    wrappingKey = await deriveWrappingKey(secret, salt, iterations)
  } finally {
    secret.fill(0)
  }
  const nowMs = options.nowMs ?? Date.now()
  const ttlMs = options.ttlMs ?? DEFAULT_UNLOCK_TTL_MS
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    throw new Error('account password confirmation ttl must be positive')
  }
  return new PreparedAccountVaultPassword(
    wrappingKey,
    { name: KDF_NAME, iterations, saltB64: toBase64Url(salt) },
    nowMs + ttlMs
  )
}

export type PreparedAccountKeyringV2 = {
  keyring: AccountKeyringV2
  privateKey: CryptoKey
}

// First-time setup consumes the account-password proof captured immediately
// after login, so the UI cannot accidentally mint a second, unrelated vault
// password. The recovery slot remains an independent user-held path.
export async function createAccountKeyringV2WithPreparedPassword(
  preparedPassword: PreparedAccountVaultPassword,
  recoveryKey: string,
  options: Pick<EncryptSeedOptions, 'iterations' | 'randomBytes'> = {}
): Promise<PreparedAccountKeyringV2> {
  if (!recoveryKey) throw new Error('recovery path required before vault storage')
  const randomBytes = options.randomBytes ?? secureRandomBytes
  const iterations = options.iterations ?? DEFAULT_ITERATIONS
  const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: ECDH_CURVE }, true, [
    'deriveBits',
  ])
  const publicRaw = new Uint8Array(await subtle().exportKey('raw', pair.publicKey))
  const privatePkcs8 = new Uint8Array(await subtle().exportKey('pkcs8', pair.privateKey))
  try {
    const passwordSlot = await preparedPassword.wrapPrivateKey(
      privatePkcs8,
      'slot-password',
      randomBytes
    )
    const recoverySlot = await wrapPrivateKeySlot(
      privatePkcs8,
      'slot-recovery_key',
      'recovery_key',
      utf8(recoveryKey),
      iterations,
      randomBytes
    )
    const publicKeyB64 = toBase64Url(publicRaw)
    await persistUnwrappedAccountPkcs8(publicKeyB64, privatePkcs8)
    return {
      keyring: {
        version: ACCOUNT_VAULT_V2,
        kdfName: KDF_NAME,
        keySlots: [passwordSlot, recoverySlot],
        publicKeyB64,
      },
      privateKey: await importAndVerifyAccountPrivateKey(privatePkcs8, publicKeyB64),
    }
  } finally {
    privatePkcs8.fill(0)
  }
}

// createAccountKeyringV2 mints the account keypair and wraps the PRIVATE key
// into user-held slots. Same mandatory-recovery gate as everywhere else.
export function attachPasskeyCredentialId(
  keyring: AccountKeyringV2,
  credentialIdB64: string
): AccountKeyringV2 {
  if (!credentialIdB64) return keyring
  return {
    ...keyring,
    keySlots: keyring.keySlots.map((slot) =>
      slot.kind === 'passkey_prf' ? { ...slot, credentialIdB64 } : slot
    ),
  }
}

export async function addLoginSlotToKeyring(
  keyring: AccountKeyringV2,
  privatePkcs8: Uint8Array,
  loginSecret: Uint8Array,
  options: Pick<EncryptSeedOptions, 'iterations' | 'randomBytes'> = {}
): Promise<AccountKeyringV2> {
  if (keyring.keySlots.some((slot) => slot.kind === 'login')) return keyring
  const randomBytes = options.randomBytes ?? secureRandomBytes
  const iterations = options.iterations ?? DEFAULT_ITERATIONS
  const loginSlot = await wrapPrivateKeySlot(
    privatePkcs8,
    'slot-login',
    'login',
    new Uint8Array(loginSecret),
    iterations,
    randomBytes
  )
  return { ...keyring, keySlots: [...keyring.keySlots, loginSlot] }
}

export async function createAccountKeyringV2(
  options: EncryptSeedOptions
): Promise<AccountKeyringV2> {
  const slots = slotInputs(options)
  const hasRecovery = slots.some(
    (slot) => slot.kind === 'recovery_key' || slot.kind === 'passkey_prf'
  )
  if (slots.length === 0) {
    throw new Error('password, passkey, login wrap or recovery key required')
  }
  if (!hasRecovery) {
    throw new Error('recovery path required before vault storage')
  }

  const randomBytes = options.randomBytes ?? secureRandomBytes
  const iterations = options.iterations ?? DEFAULT_ITERATIONS

  const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: ECDH_CURVE }, true, [
    'deriveBits',
  ])
  const publicRaw = new Uint8Array(await subtle().exportKey('raw', pair.publicKey))
  const privatePkcs8 = new Uint8Array(await subtle().exportKey('pkcs8', pair.privateKey))

  try {
    const keySlots = await Promise.all(
      slots.map(async (slot) => {
        const salt = randomBytes(16)
        const nonce = randomBytes(12)
        const wrappingKey = await deriveWrappingKey(slot.secret, salt, iterations)
        const wrapped = new Uint8Array(
          await subtle().encrypt(
            { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
            wrappingKey,
            toArrayBuffer(privatePkcs8)
          )
        )
        return {
          id: `slot-${slot.kind}`,
          kind: slot.kind,
          kdf: { name: KDF_NAME, iterations, saltB64: toBase64Url(salt) },
          cipher: { name: CIPHER_NAME, nonceB64: toBase64Url(nonce) },
          wrappedKeyB64: toBase64Url(wrapped),
        } satisfies VaultKeySlot
      })
    )

    const publicKeyB64 = toBase64Url(publicRaw)
    await persistUnwrappedAccountPkcs8(publicKeyB64, privatePkcs8)
    return {
      version: ACCOUNT_VAULT_V2,
      kdfName: KDF_NAME,
      keySlots,
      publicKeyB64,
    }
  } finally {
    privatePkcs8.fill(0)
  }
}

// unlockAccountKeyringV2 unwraps the private key with any user-held secret.
// The returned CryptoKey is non-extractable: it can derive, never be exported.
export async function unlockAccountKeyringV2(
  keyring: AccountKeyringV2,
  options: DecryptSeedOptions
): Promise<CryptoKey> {
  if (keyring.version !== ACCOUNT_VAULT_V2) {
    throw new Error(`unsupported account vault version: ${keyring.version}`)
  }
  const privatePkcs8 = await unwrapAccountPrivateKey(keyring, options)
  try {
    await persistUnwrappedAccountPkcs8(keyring.publicKeyB64, privatePkcs8)
    return await importAndVerifyAccountPrivateKey(privatePkcs8, keyring.publicKeyB64)
  } finally {
    privatePkcs8.fill(0)
  }
}

export type RewrappedAccountKeyringV2 = {
  keyring: AccountKeyringV2
  previousPasswordSlot: VaultKeySlot
  privateKey: CryptoKey
}

// Re-wraps only the v2 password slot. The ECIES keypair, public key and every
// recovery/passkey slot remain byte-for-byte unchanged, so all existing wallet
// envelopes continue to decrypt with the same private key.
export async function rewrapAccountKeyringV2PasswordWithPrepared(
  keyring: AccountKeyringV2,
  oldSecret: DecryptSeedOptions,
  preparedAccountPassword: PreparedAccountVaultPassword,
  randomBytes: (size: number) => Uint8Array = secureRandomBytes
): Promise<RewrappedAccountKeyringV2> {
  if (keyring.version !== ACCOUNT_VAULT_V2) {
    throw new Error(`unsupported account vault version: ${keyring.version}`)
  }
  const passwordSlots = keyring.keySlots.filter((slot) => slot.kind === 'password')
  if (passwordSlots.length !== 1) {
    throw new Error('account vault must contain exactly one password slot')
  }
  const previousPasswordSlot = passwordSlots[0]
  if (!previousPasswordSlot) throw new Error('account vault password slot missing')

  const privatePkcs8 = await unwrapAccountPrivateKey(keyring, oldSecret)
  try {
    await persistUnwrappedAccountPkcs8(keyring.publicKeyB64, privatePkcs8)
    const privateKey = await importAndVerifyAccountPrivateKey(privatePkcs8, keyring.publicKeyB64)
    const nextPasswordSlot = await preparedAccountPassword.wrapPrivateKey(
      privatePkcs8,
      previousPasswordSlot.id,
      randomBytes
    )
    return {
      keyring: {
        ...keyring,
        keySlots: keyring.keySlots.map((slot) =>
          slot === previousPasswordSlot ? nextPasswordSlot : slot
        ),
      },
      previousPasswordSlot,
      privateKey,
    }
  } finally {
    privatePkcs8.fill(0)
  }
}

export async function rewrapAccountKeyringV2Password(
  keyring: AccountKeyringV2,
  oldSecret: DecryptSeedOptions,
  accountPassword: string,
  options: PrepareAccountVaultPasswordOptions = {}
): Promise<AccountKeyringV2> {
  const prepared = await prepareAccountVaultPassword(accountPassword, options)
  try {
    return (
      await rewrapAccountKeyringV2PasswordWithPrepared(
        keyring,
        oldSecret,
        prepared,
        options.randomBytes
      )
    ).keyring
  } finally {
    prepared.dispose()
  }
}

async function unwrapAccountPrivateKey(
  keyring: AccountKeyringV2,
  options: DecryptSeedOptions
): Promise<Uint8Array> {
  const inputs = slotInputs(options)
  const available = new Map(inputs.map((slot) => [slot.kind, slot.secret]))
  try {
    for (const slot of keyring.keySlots) {
      const secret = available.get(slot.kind)
      if (!secret) {
        continue
      }
      validateAccountVaultKeySlot(slot)
      try {
        const wrappingKey = await deriveWrappingKey(
          secret,
          fromBase64Url(slot.kdf.saltB64),
          slot.kdf.iterations
        )
        const pkcs8 = new Uint8Array(
          await subtle().decrypt(
            { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(slot.cipher.nonceB64)) },
            wrappingKey,
            toArrayBuffer(fromBase64Url(slot.wrappedKeyB64))
          )
        )
        return pkcs8
      } catch (error) {
        if (!isCiphertextAuthenticationFailure(error)) throw error
      }
    }
    throw new AccountVaultSecretMismatchError()
  } finally {
    for (const input of inputs) input.secret.fill(0)
  }
}

function validateAccountVaultKeySlot(slot: VaultKeySlot): void {
  if (!slot.id || slot.kdf.name !== KDF_NAME) throw new Error('account vault key slot is invalid')
  if (!Number.isInteger(slot.kdf.iterations) || slot.kdf.iterations <= 0) {
    throw new Error('account vault key slot iterations are invalid')
  }
  if (fromBase64Url(slot.kdf.saltB64).length < 16) {
    throw new Error('account vault key slot salt is invalid')
  }
  if (slot.cipher.name !== CIPHER_NAME || fromBase64Url(slot.cipher.nonceB64).length !== 12) {
    throw new Error('account vault key slot cipher is invalid')
  }
  if (fromBase64Url(slot.wrappedKeyB64).length <= 16) {
    throw new Error('account vault key slot ciphertext is invalid')
  }
}

function isCiphertextAuthenticationFailure(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'OperationError'
}

async function wrapPrivateKeySlot(
  privatePkcs8: Uint8Array,
  id: string,
  kind: VaultKeySlotKind,
  secret: Uint8Array,
  iterations: number,
  randomBytes: (size: number) => Uint8Array
): Promise<VaultKeySlot> {
  const salt = randomBytes(16)
  const nonce = randomBytes(12)
  try {
    const wrappingKey = await deriveWrappingKey(secret, salt, iterations)
    const wrapped = new Uint8Array(
      await subtle().encrypt(
        { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
        wrappingKey,
        toArrayBuffer(privatePkcs8)
      )
    )
    return {
      id,
      kind,
      kdf: { name: KDF_NAME, iterations, saltB64: toBase64Url(salt) },
      cipher: { name: CIPHER_NAME, nonceB64: toBase64Url(nonce) },
      wrappedKeyB64: toBase64Url(wrapped),
    }
  } finally {
    secret.fill(0)
  }
}

async function persistUnwrappedAccountPkcs8(
  publicKeyB64: string,
  privatePkcs8: Uint8Array
): Promise<void> {
  try {
    await saveDevicePkcs8Backup(publicKeyB64, privatePkcs8)
  } catch {
    // Origin backup is a hard-refresh convenience. Unlock still succeeds.
  }
}

async function importAndVerifyAccountPrivateKey(
  privatePkcs8: Uint8Array,
  publicKeyB64: string
): Promise<CryptoKey> {
  const privateKey = await subtle().importKey(
    'pkcs8',
    toArrayBuffer(privatePkcs8),
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    false,
    ['deriveBits']
  )
  const publicKey = await subtle().importKey(
    'raw',
    toArrayBuffer(fromBase64Url(publicKeyB64)),
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    false,
    []
  )
  const probe = await subtle().generateKey({ name: 'ECDH', namedCurve: ECDH_CURVE }, false, [
    'deriveBits',
  ])
  const fromPrivate = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: probe.publicKey }, privateKey, 256)
  )
  const fromPublic = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: publicKey }, probe.privateKey, 256)
  )
  let mismatch = fromPrivate.length ^ fromPublic.length
  for (let i = 0; i < Math.min(fromPrivate.length, fromPublic.length); i++) {
    mismatch |= (fromPrivate[i] ?? 0) ^ (fromPublic[i] ?? 0)
  }
  fromPrivate.fill(0)
  fromPublic.fill(0)
  if (mismatch !== 0) throw new Error('account vault private key does not match public key')
  return privateKey
}

async function eciesAesKey(
  privateKey: CryptoKey,
  peerPublicKey: CryptoKey,
  usages: KeyUsage[]
): Promise<CryptoKey> {
  const shared = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: peerPublicKey }, privateKey, 256)
  )
  const hkdfKey = await subtle().importKey('raw', toArrayBuffer(shared), 'HKDF', false, [
    'deriveKey',
  ])
  shared.fill(0)
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new ArrayBuffer(0),
      info: toArrayBuffer(utf8(ECIES_HKDF_INFO)),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    usages
  )
}

// encryptSeedToAccountVault: NO password, NO unlock — just the public key.
export async function encryptSeedToAccountVault(
  publicKeyB64: string,
  seedMaterial: string
): Promise<AccountSecretEnvelope> {
  if (!seedMaterial) {
    throw new Error('seed material required')
  }
  const recipient = await subtle().importKey(
    'raw',
    toArrayBuffer(fromBase64Url(publicKeyB64)),
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    false,
    []
  )
  const ephemeral = await subtle().generateKey({ name: 'ECDH', namedCurve: ECDH_CURVE }, true, [
    'deriveBits',
  ])
  const key = await eciesAesKey(ephemeral.privateKey, recipient, ['encrypt'])
  const nonce = secureRandomBytes(12)
  const ct = new Uint8Array(
    await subtle().encrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
      key,
      toArrayBuffer(utf8(seedMaterial))
    )
  )
  const epk = new Uint8Array(await subtle().exportKey('raw', ephemeral.publicKey))
  const payload = new Uint8Array(epk.length + ct.length)
  payload.set(epk, 0)
  payload.set(ct, epk.length)

  return {
    version: VAULT_VERSION,
    cipherName: CIPHER_NAME,
    nonceB64: toBase64Url(nonce),
    ciphertextB64: toBase64Url(payload),
  }
}

// decryptSeedFromAccountVault: needs the unlocked private key (password path).
export async function decryptSeedFromAccountVault(
  privateKey: CryptoKey,
  envelope: Pick<AccountSecretEnvelope, 'nonceB64' | 'ciphertextB64'>
): Promise<string> {
  const payload = fromBase64Url(envelope.ciphertextB64)
  if (payload.length <= EPK_BYTES) {
    throw new Error('account-ecies envelope too short')
  }
  const ephemeralPublic = await subtle().importKey(
    'raw',
    toArrayBuffer(payload.slice(0, EPK_BYTES)),
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    false,
    []
  )
  const key = await eciesAesKey(privateKey, ephemeralPublic, ['decrypt'])
  const plain = new Uint8Array(
    await subtle().decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(fromBase64Url(envelope.nonceB64)) },
      key,
      toArrayBuffer(payload.slice(EPK_BYTES))
    )
  )
  return new TextDecoder().decode(plain)
}

// AccountUnlockSession holds the unlocked PRIVATE key in memory only. It is
// non-extractable, so even an XSS cannot exfiltrate the raw bytes — only use
// it while the tab lives.
export class AccountUnlockSession {
  #privateKey: CryptoKey | null
  #expiresAtMs: number

  constructor(privateKey: CryptoKey, ttlMs = Number.MAX_SAFE_INTEGER, nowMs = Date.now()) {
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
      throw new Error('unlock ttl must be positive')
    }
    this.#privateKey = privateKey
    // A live login is the trading authorization. The 15-minute v1 DEK TTL is
    // leftover confirm-it's-you: after it fired, status stayed "unlocked"
    // while the key was gone, and the next trade printed "Could not sign".
    this.#expiresAtMs = nowMs + ttlMs
  }

  isUnlocked(nowMs = Date.now()): boolean {
    if (!this.#privateKey) {
      return false
    }
    if (nowMs > this.#expiresAtMs) {
      this.lock()
      return false
    }
    return true
  }

  async decryptSeed(
    envelope: Pick<AccountSecretEnvelope, 'nonceB64' | 'ciphertextB64'>
  ): Promise<string> {
    if (!this.isUnlocked() || !this.#privateKey) {
      throw new Error('account vault session locked')
    }
    return decryptSeedFromAccountVault(this.#privateKey, envelope)
  }

  lock(): void {
    this.#privateKey = null
  }

  toJSON(): Record<string, never> {
    return {}
  }
}
